import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

export interface SolarData {
  pvYieldW:           number;
  batteryDischargeW:  number;
  batteryPercent:     number;
  gridConsumptionW:   number;
  totalConsumptionW:  number;
  batteryCapacityWh:  number;
  consumptionL1W:     number;
  consumptionL2W:     number;
  consumptionL3W:     number;
}

export interface ThermometerReading {
  name:       string;
  deviceId:   string;
  source:     'govee' | 'knx';   // Govee thermometer or KNX heating controller (Gira HomeServer)
  tempC:      number;
  humidity:   number | null;     // KNX: only rooms whose sensor also sends humidity on the bus (else null)
  setpointC?: number | null;     // KNX heating target (Soll)
  floor?:     string;            // KNX floor, e.g. "E3"
  room?:      string;            // KNX room, e.g. "Wohnzimmer"
  /** KNX: the room's heating valve position (0..100 %, PWM channels as 30-min duty); only while the
   *  mapped valve (see valveStatus) has a fresh value (≤ 15 min). Absent without a mapped valve. */
  valvePct?:    number | null;
  /** How the valve ↔ room mapping is known: admin-confirmed, read from the heating actuator's own
   *  tables ('verified'), from the ETS book name, or learned with a clear margin. Learning candidates
   *  stay admin-only and never reach the public reading. */
  valveStatus?: KnxValveStatus | null;
  /** /api/history series of the mapped valve (e.g. "knxga-16-1-0"): the chart's valve history (the
   *  points' own valvePct does not say which valve it came from — it may predate a re-mapping). */
  valveSeries?: string | null;
  /** True for a 2-point / PWM valve (its archive switches 0 ↔ 100 %: charted as the 30-min duty). */
  valvePwm?:    boolean | null;
}

/** Public status of a room's valve mapping ('candidate' only appears in the admin view). 'verified' =
 *  read from the actuator's group-address / association / group-object tables: a given fact, shown
 *  like 'confirmed'. */
export type KnxValveStatus = 'confirmed' | 'verified' | 'likely' | 'book' | 'candidate';

export interface TempDataPoint {
  ts:         number;
  tempC:      number;
  humidity:   number | null;
  setpointC?: number | null;     // KNX only
  valvePct?:  number | null;     // KNX only: mapped valve position 0..100 % (of the valve mapped when the point was recorded)
}

/** One KNX light, socket or blind (Gira HomeServer, read-only). Fields the backend has no value for are omitted. */
export interface KnxDeviceState {
  id:          string;                         // "knx-<functionUid>"
  kind:        'light' | 'socket' | 'blind';
  name:        string;
  floor?:      string;                         // e.g. "E3", "Außen"; missing when not placed
  room?:       string;                         // missing when not placed or placed on a floor only
  on?:         boolean;                        // lights / sockets; missing when unknown
  brightness?: number;                         // dimmers: 0..100 %
  dimmable?:   boolean;                        // dimmers only
  position?:   number;                         // blinds: 0..100 % (0 = fully open / up, 100 = fully closed / down)
  since?:      number;                         // epoch ms of the last observed change (since backend start)
}

/** All KNX lights, sockets and blinds, each list in HomeServer location order (unplaced devices last). */
export interface KnxDevicesSnapshot {
  lights:    KnxDeviceState[];
  sockets:   KnxDeviceState[];
  blinds:    KnxDeviceState[];
  /** Last poll round that reached the HomeServer, epoch ms shifted to the client clock (via the frame's serverTime). */
  updatedAt: number;
}

/** What a weather-station channel measures, as classified by the backend (ETS book or heuristics). */
export type KnxWeatherQuantity =
  'outdoorTemperature' | 'temperature' | 'humidity' | 'windSpeed' | 'windGust' |
  'brightness' | 'rain' | 'binary' | 'pressure' | 'percent' | 'unknown';

/** 'book' = named / typed in the ETS GA book; 'likely' / 'unsure' = inferred from the overheard values. */
export type KnxWeatherConfidence = 'book' | 'likely' | 'unsure';

/** One group address the KNX weather station sends (passively overheard on the bus). */
export interface KnxWeatherChannel {
  ga:          string;                        // "8/0/5"
  name?:       string | null;                 // ETS GA book name, when the book has one
  quantity:    KnxWeatherQuantity;
  confidence:  KnxWeatherConfidence;
  unit?:       string | null;                 // "°C", "%", "m/s", "lx", …
  dpt?:        string | null;                 // "9", "5.001", "1.001", …
  value:       number | boolean | null;       // binary channels: boolean (or 0 / 1)
  text?:       string | null;                 // backend-formatted value
  ts:          number | null;                 // last telegram, epoch ms (client clock)
  changedAt?:  number | null;                 // last change of the value, epoch ms (client clock; backend seeds it from 30 days of archive)
  min24h?:     number | null;
  max24h?:     number | null;
  series:      string;                        // archive series for /api/history, e.g. "knxga-8-0-5"
}

/** The "knxWeather" frame: the weather station and its channels (device null / no channels = none known). */
export interface KnxWeatherSnapshot {
  device:    string | null;                   // individual address, e.g. "1.3.10"
  line?:     string | null;                   // e.g. "1.3"
  updatedAt: number | null;                   // last telegram of any channel, epoch ms (client clock)
  channels:  KnxWeatherChannel[];
}

export type DayFaultReason = 'WIFI_OUTAGE' | 'TIGO_OUTAGE' | 'DATA_MISSING' | 'DEYE_FREEZE' | 'NO_YIELD';

export interface DayStat {
  date:             string;
  generationKwh:    number;
  consumptionKwh:   number;
  gridImportKwh:    number;
  gridExportKwh:    number;
  autarkiePercent:  number;
  faultReason?:     DayFaultReason | null;
  faultComment?:    string | null;
  sunriseUtc?:      string | null;
  sunsetUtc?:       string | null;
}

export interface HourlyDataPoint {
  ts:             number;  // epoch ms
  generationKwh:  number;
  consumptionKwh: number;
  gridImportKwh:  number;
  gridExportKwh:  number;
}

export interface WastePickup {
  date: string;   // ISO: YYYY-MM-DD
  type: string;   // e.g. PPK, Bio, GT, RM, A, SoH, TB
  name: string;   // German name from calendar
}

export interface SolarHistoryPoint {
  ts:               number;
  pvYieldW:         number;
  batteryDischargeW: number;
  gridConsumptionW: number;
  totalConsumptionW: number;
  batteryPercent:   number;
  consumptionL1W:   number;
  consumptionL2W:   number;
  consumptionL3W:   number;
}

export interface FroelingError {
  code:      string;
  message:   string;
  severity:  string;
  timestamp: string;
  /** false: no longer pending at the controller (only not yet acknowledged); null/absent: unknown → treated as active. */
  active?:         boolean | null;
  unacknowledged?: boolean | null;
}

export interface FroelingHeatingCircuit {
  name:             string;
  flowTempActualC:  number;
  flowTempTargetC:  number;
  pumpState:        string;
  /** Betriebsart code (0 AUS, 1 Auto, 2 Extraheizen, 3 Absenken, 4 Dauerabsenken, 5 Party) and the controller's text. */
  mode?:               string;
  modeText?:           string;
  /** Heating curve: desired flow temperature at −10 °C / +10 °C outdoor. */
  flowTempAtMinus10C?: number;
  flowTempAtPlus10C?:  number;
}

/** Solar-thermal heat quantity as the Fröling lists it. */
export interface FroelingSolarYield {
  yesterdayKwh?:       number;
  days:                { daysAgo: number; kwh: number }[];
  totalKwh?:           number;
  collectorPumpHours?: number;
}

export interface FroelingData {
  boilerTempC:          number;
  exhaustTempC:         number;
  boilerState:          string;
  boilerModulationPct:  number;
  returnTempC:          number;
  oxygenPct:            number;
  draftFanPct:          number;
  operatingHours:       number;
  bufferTopTempC:       number;
  bufferBottomTempC:    number;
  bufferChargePct:      number;
  bufferTempC:          number;
  outdoorTempC:         number;
  hotWaterTempC:        number;
  flowTempActualC:      number;
  flowTempTargetC:      number;
  pumpState:            string;
  /** Fill of the boiler's own ~44 kg pellet hopper (refilled from the store by suction) — not the store level. */
  pelletLevelPct:       number;
  /** Cumulative pellet counter of the boiler (kg, 1 kg steps). */
  pelletConsumptionKg:  number;
  /** Solar collector temperature. */
  solarThermalTempC:    number;
  /** Collector pump control (%). */
  solarThermalPumpPct:  number;
  /** Speed of the DHW circulation pump (%). */
  circulationPumpPct:   number;
  heatingCircuits:      FroelingHeatingCircuit[];
  errors:               FroelingError[];
  // ── later fields (missing = not reported / an older backend) ──
  /** Kesselzustand code and the controller's text ("Betriebsbereit"). */
  boilerStateCode?:     number;
  boilerStateText?:     string;
  /** Kessel Zustand (0 Dauerlast, 1 Brauchwasser, 2 Automatik). */
  boilerMode?:          string;
  boilerModeText?:      string;
  boilerSetTempC?:      number;
  ashHoursLeft?:        number;
  /** Buffer loading pump (boiler → buffer), %. */
  bufferPumpPct?:       number;
  /** DHW tank loading pump, %. */
  dhwPumpPct?:          number;
  /** DHW tank lower (solar reference) sensor. */
  dhwSolarRefTempC?:    number;
  dhwSetTempC?:         number;
  dhwReloadBelowC?:     number;
  dhwMode?:             string;
  dhwModeText?:         string;
  collectorFlowTempC?:  number;
  collectorReturnTempC?: number;
  solarBufferTopTempC?: number;
  solarBufferBottomTempC?: number;
  solarYield?:          FroelingSolarYield;
  /** Return of the circulation line; circulationReturnNoSensor: it reads 0 °C constantly (no sensor). */
  circulationReturnTempC?: number;
  circulationReturnNoSensor?: boolean;
  circulationFlowSwitch?: number;
  /** The pellet STORE (Brennstofflager) remaining / minimum, tons — not the boiler hopper. */
  storeRemainingT?:     number;
  storeMinT?:           number;
}

/** One Fröling setting (/api/data/froeling-settings). */
export interface FroelingSetting {
  key:       string;
  label:     string;
  value:     string;
  /** The meaning of a code ("Auto"). */
  text?:     string;
  unit?:     string;
  editable:  boolean;
  min?:      string;
  max?:      string;
}

/** The settings of one Fröling component. */
export interface FroelingSettingsGroup {
  componentId:  string;
  name:         string;
  type:         string;
  fetchedAt:    number;
  settings:     FroelingSetting[];
  timeWindows?: { weekDay: string; phases: string[] }[];
}

/** One noticed change of a Fröling setting. */
export interface FroelingSettingChange {
  ts:          number;
  componentId: string;
  component:   string;
  key:         string;
  label:       string;
  unit?:       string;
  oldValue:    string;
  newValue:    string;
  oldText?:    string;
  newText?:    string;
}

/** 'froelingSettings' frame / /api/data/froeling-settings: read-only snapshot + change log (newest first). */
export interface FroelingSettingsSnapshot {
  fetchedAt:  number;
  /** Changes are recorded since (epoch ms). */
  since:      number;
  components: FroelingSettingsGroup[];
  changes:    FroelingSettingChange[];
}

/** Pellet use of one Europe/Berlin day ('pelletStats' frame / /api/data/pellets); one row per calendar day. */
export interface PelletDayStat {
  date:           string;
  consumptionKg:  number;
  /** The boiler hopper (%) at the day's last reading — not the store. null = no reading that day. */
  levelPct:       number | null;
  /** Hours the burner ran; null on legacy rows that predate the archive. */
  burnHours?:      number | null;
  /** kg per hour while burning (~6); null without (enough) burning. */
  avgBurnRateKgH?: number | null;
  /** Data missing for 15+ min that day (kg burnt in the gap is booked on the day the counter was read again). */
  dataGap?:        boolean;
  /** Today: still growing. */
  inProgress?:     boolean;
}

/** Live pellet figures ('pelletUsage' frame / /api/data/pellet-usage). */
export interface PelletUsageSummary {
  burning:         boolean;
  /** Current burn rate (kg/h), 0 when the burner is off. */
  currentRateKgH:  number;
  /** Recent kg per burning hour. */
  burnRateKgH:     number;
  burnSinceTs:     number | null;
  todayKg:         number;
  todayBurnHours:  number;
  last24hKg:       number;
  hopperPct:       number | null;
  counterKg:       number | null;
  lastTickTs:      number | null;
  updatedTs:       number | null;
  ready?:          boolean;
}

/** One bucket of /api/pellets/usage. */
export interface PelletUsageBucket {
  ts:            number;
  kg:            number;
  /** Mean kg/h over the bucket (null where nothing is known). */
  rateKgH:       number | null;
  /** kg per hour while burning (null without burning). */
  burnRateKgH:   number | null;
  burnMinutes:   number | null;
  provisional:   boolean;
  /** 10min/hour, only when provisional: estimated kg not counted yet (in the rates, not in kg). */
  provisionalKg?: number;
  gap:           boolean;
}

export interface FroelingHistoryPoint {
  ts:                   number;
  boilerTempC:          number;
  exhaustTempC:         number;
  boilerModulationPct:  number;
  bufferTopTempC:       number;
  bufferBottomTempC:    number;
  bufferChargePct:      number;
  hotWaterTempC:        number;
  flowTempActualC:      number;
  outdoorTempC:         number;
  solarThermalTempC:    number;
  pelletConsumptionKg:  number;
  /** The boiler hopper (%), not the store. */
  pelletLevelPct:       number;
  /** Speed of the DHW circulation pump (%). */
  circulationPumpPct:   number;
  heatingCircuits:      { name: string; flowTempActualC: number; flowTempTargetC?: number; pumpState?: string }[];
  // ── later fields: missing on older points ──
  /** Kesselzustand (a category: a downsampled bucket carries its most frequent code). */
  boilerStateCode?:     number;
  /** 1 = the burner burns, 0 = not; a downsampled bucket: the share of the time it burned (0–1). */
  burnerOn?:            number;
  draftFanPct?:         number;
  oxygenPct?:           number;
  ashHoursLeft?:        number;
  bufferPumpPct?:       number;
  dhwPumpPct?:          number;
  dhwSolarRefTempC?:    number;
  solarThermalPumpPct?: number;
  collectorFlowTempC?:  number;
  collectorReturnTempC?: number;
  solarBufferTopTempC?: number;
  solarBufferBottomTempC?: number;
  circulationReturnTempC?: number;
  circulationFlowSwitch?: number;
  storeRemainingT?:     number;
}

export interface UnifiDevice {
  name:       string;
  type:       string;    // uap, usw, ugw, udm, uxg
  model:      string;
  shortname:  string;
  online:     boolean;
  uptime:     number;    // seconds
  ip:         string;
  numClients: number;    // -1 if not applicable
  rxBps:      number;
  txBps:      number;
}

export interface UnifiClient {
  hostname: string;
  ip:       string;
  isWifi:   boolean;
  signal:   number;    // dBm (0 for wired)
  rxBytes:  number;
  txBytes:  number;
  rxBps:    number;
  txBps:    number;
}

export interface UnifiSnapshot {
  wanUp:        boolean;
  wanRxBps:     number;
  wanTxBps:     number;
  wanUptime:    number;   // seconds
  wanLatency:   number;   // ms, -1 = unknown
  wanIp:        string | null;
  wanIsp:       string | null;
  totalClients: number;
  lanClients:   number;
  wlanClients:  number;
  devices:      UnifiDevice[];
  topClients:   UnifiClient[];
}

export interface ZisterneData {
  levelPct:  number;
  depthCm:   number;
  battery:   number;
  dummy?:    boolean;
}

export interface ZisterneHistoryPoint {
  ts:       number;
  levelPct: number;
  depthCm:  number;
}

@Injectable({ providedIn: 'root' })
export class VersionService implements OnDestroy {

  readonly newVersion$          = new Subject<string>();
  readonly solar$               = new Subject<SolarData>();
  readonly solarHistory$        = new Subject<SolarHistoryPoint[]>();
  readonly thermometers$        = new Subject<ThermometerReading[]>();
  readonly thermometerHistory$  = new Subject<{[deviceId: string]: TempDataPoint[]}>();
  readonly thermometerAppend$   = new Subject<{[deviceId: string]: TempDataPoint}>();
  readonly stats$               = new Subject<DayStat[]>();
  readonly waste$               = new Subject<WastePickup[]>();
  readonly unifi$               = new Subject<UnifiSnapshot>();
  readonly froeling$            = new Subject<FroelingData>();
  readonly froelingHistory$     = new Subject<FroelingHistoryPoint[]>();
  readonly pelletStats$         = new Subject<PelletDayStat[]>();
  readonly pelletUsage$         = new Subject<PelletUsageSummary>();
  readonly froelingSettings$    = new Subject<FroelingSettingsSnapshot>();
  readonly zisterne$            = new Subject<ZisterneData>();
  readonly zisterneHistory$     = new Subject<ZisterneHistoryPoint[]>();
  readonly knxDevices$          = new Subject<KnxDevicesSnapshot>();
  readonly knxWeather$          = new Subject<KnxWeatherSnapshot>();

  connectedClients = 0;
  sunrise: string | null = null;
  sunset:  string | null = null;

  private knownCommit: string | null = null;
  private worker!: Worker;

  frontendCommit    = '';
  backendCommit     = '';
  connected         = true;
  wsConnectedOnce   = false;

  // Centralized new-version reload state, shared by the dashboard and Claudia overlays.
  reloadPending     = false;
  reloadCountdown   = 5;
  private reloadTimer: any = null;
  private started   = false;

  constructor(private zone: NgZone) {}

  connect(frontendCommit: string): void {
    if (this.started) return;            // idempotent: one worker/WS per page load
    this.started = true;
    this.frontendCommit = frontendCommit;

    this.worker = new Worker(new URL('./version.worker', import.meta.url), { type: 'module' });

    this.worker.onmessage = ({ data }) => {
      this.zone.run(() => {
        if      (data.kind === 'data')         this.handleMessage(data.payload);
        else if (data.kind === 'connected')    { this.connected = true; this.wsConnectedOnce = true; }
        else if (data.kind === 'disconnected') this.connected = false;
        else if (data.kind === 'error')        this.connected = false;
      });
    };
  }

  private handleMessage(msg: any): void {
    switch (msg.type) {
      case 'hello':
        this.backendCommit = msg.commit;
        // First hello on page load: record, never reload. A different commit on a
        // later hello means the backend restarted → show the update overlay.
        if (!this.knownCommit) this.knownCommit = msg.commit;
        else if (msg.commit !== this.knownCommit) { this.newVersion$.next(msg.commit); this.triggerReload(); }
        break;
      case 'update':          this.newVersion$.next(''); this.triggerReload(); break;  // backend about to restart
      case 'solar':           this.solar$.next(msg as SolarData); break;
      case 'solarHistory':    this.solarHistory$.next(msg.points as SolarHistoryPoint[]); break;
      case 'thermometers':
        // Handshake carries the full 24h `history`; each poll frame only `append`s its new points.
        this.thermometers$.next(msg.sensors as ThermometerReading[]);
        if (msg.history) this.thermometerHistory$.next(msg.history);
        if (msg.append)  this.thermometerAppend$.next(msg.append);
        break;
      case 'stats':           this.stats$.next(msg.days as DayStat[]); break;
      case 'clients':         this.connectedClients = msg.count; break;
      case 'sunrise':         this.sunrise = msg.sunrise; this.sunset = msg.sunset; break;
      case 'waste':           this.waste$.next(msg.pickups as WastePickup[]); break;
      case 'unifi':           this.unifi$.next(msg.data as UnifiSnapshot); break;
      case 'froeling':        this.froeling$.next(msg.data as FroelingData); break;
      case 'froelingHistory': this.froelingHistory$.next(msg.points as FroelingHistoryPoint[]); break;
      case 'pelletStats':     this.pelletStats$.next(msg.days as PelletDayStat[]); break;
      case 'pelletUsage':     this.pelletUsage$.next(msg.data as PelletUsageSummary); break;
      case 'froelingSettings': if (msg.data) this.froelingSettings$.next(msg.data as FroelingSettingsSnapshot); break;
      case 'zisterne':        this.zisterne$.next(msg.data as ZisterneData); break;
      case 'zisterneHistory': this.zisterneHistory$.next(msg.points as ZisterneHistoryPoint[]); break;
      case 'knxDevices':      this.knxDevices$.next(VersionService.knxSnapshot(msg)); break;
      case 'knxWeather':      this.knxWeather$.next(VersionService.knxWeather(msg)); break;
    }
  }

  /** "knxDevices" frame → snapshot; updatedAt is moved onto the client clock so "updated ago" survives clock skew. */
  private static knxSnapshot(msg: any): KnxDevicesSnapshot {
    const now  = Date.now();
    const list = (v: unknown): KnxDeviceState[] => Array.isArray(v) ? v as KnxDeviceState[] : [];
    const skew = typeof msg.serverTime === 'number' ? now - msg.serverTime : 0;
    const updatedAt = typeof msg.updatedAt === 'number' ? Math.min(now, msg.updatedAt + skew) : now;
    return { lights: list(msg.lights), sockets: list(msg.sockets), blinds: list(msg.blinds), updatedAt };
  }

  /**
   * "knxWeather" frame → snapshot. The snapshot may come wrapped in `data` or flat next to `type`;
   * every timestamp is moved onto the client clock (via the frame's serverTime) so "updated ago" and
   * the chart windows survive clock skew.
   */
  private static knxWeather(msg: any): KnxWeatherSnapshot {
    const src  = [msg?.data, msg?.weather].find(v => v && typeof v === 'object' && !Array.isArray(v)) ?? msg;
    const now  = Date.now();
    const skew = typeof msg.serverTime === 'number' ? now - msg.serverTime : 0;
    const ts   = (v: unknown): number | null => typeof v === 'number' && v > 0 ? Math.min(now, v + skew) : null;
    const num  = (v: unknown): number | null => typeof v === 'number' && Number.isFinite(v) ? v : null;
    const str  = (v: unknown): string | null => typeof v === 'string' && v ? v : null;
    const channels: KnxWeatherChannel[] = (Array.isArray(src?.channels) ? src.channels : [])
      .filter((c: any) => c && typeof c.ga === 'string')
      .map((c: any): KnxWeatherChannel => ({
        ga:         c.ga,
        name:       str(c.name),
        quantity:   typeof c.quantity === 'string' ? c.quantity : 'unknown',
        confidence: c.confidence === 'book' || c.confidence === 'likely' ? c.confidence : 'unsure',
        unit:       str(c.unit),
        dpt:        str(c.dpt),
        value:      typeof c.value === 'boolean' ? c.value : num(c.value),
        text:       str(c.text),
        ts:         ts(c.ts),
        changedAt:  ts(c.changedAt),
        min24h:     num(c.min24h),
        max24h:     num(c.max24h),
        series:     str(c.series) ?? 'knxga-' + c.ga.split('/').join('-'),
      }));
    return { device: str(src?.device), line: str(src?.line), updatedAt: ts(src?.updatedAt), channels };
  }

  /** Show the reload overlay and reload after a short countdown (once). */
  private triggerReload(): void {
    if (this.reloadPending) return;
    this.reloadPending = true;
    this.reloadCountdown = 5;
    this.reloadTimer = setInterval(() => {
      this.reloadCountdown--;
      if (this.reloadCountdown <= 0) { clearInterval(this.reloadTimer); location.reload(); }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
    if (this.reloadTimer) clearInterval(this.reloadTimer);
  }
}
